import { Box, Stack, Typography, Tooltip, IconButton, List, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import ReplayIcon from "@mui/icons-material/Replay";
import { FC, useEffect } from "react";
import { ExclusionRule } from "@/gen/api/securefile";
import { useZSliceUnderContext } from "@/utils/zustand-form/hooks";
import { CollectEditsProtocol, CollectEditsMetadata } from "@/utils/zustand-form/ext/collectEdits";
import { FormToken } from "@/utils/zustand-form/types";

import ExclusionRuleLine from './ExclusionRuleLine';
import { EditorLineSx } from './InfoTypeEditor.types';


const ExclusionRuleEditor : FC<{
  parentToken?: FormToken,
  baseData: ExclusionRule[],
}> = ({parentToken, baseData}) => {
  const utils = useZSliceUnderContext({
    parentToken: parentToken ?? null,
    pathFragment: 'exclusionRules',
    prevData: baseData,
    stageInit: () => ({
      added: [] as ExclusionRule[], // sparse array
      removed: new Set<number>(),
    }),
    extraStates: {},
  });
  const { states, setStates, token, installProtocolMetadata } = utils;

  useEffect(() => {
    return installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => { /* No top-level validation */ },
      overrideCollect: async (node, safeInvokeNode, safeChildrenCollection) => {
        const stage = node.state.stage;
        const prevData = node.state.prevData;
        const ids = [
          ...[...new Array(prevData.length)].map((_i: number, i: number) => i)
            .filter((i: number) => !stage.removed.has(i))
            .map((i: number) => `r[${i}]`),
          ...stage.added.map((_d: ExclusionRule, i: number) => `a[${i}]`).filter((_d: ExclusionRule, i: number) => stage.added[i] != null),
        ];
        const childNodes = new Map(
          Array.from(safeChildrenCollection.values()).map((cnode) => [cnode.token.path.split('.').pop() ?? cnode.token.path, cnode])
        );
        const collected = [];
        for (const id of ids) {
          const childNode = childNodes.get(id);
          if (childNode) {
            const result = await safeInvokeNode(childNode);
            collected.push(result.newValue ?? result);
          }
        }
        return collected;
      },
    });
  }, [installProtocolMetadata]);

  useEffect(() => {
    setStates(v => ({...v, prevData: baseData, stage: {added: [], removed: new Set()}, touched: {}}))
  }, [baseData, setStates])

  const actionBtn = (d: ExclusionRule, i:number) => {
    const atAdded = states.stage.added.indexOf(d)
    const isRestore = states.stage.removed.has(i)
    const actionFn = () => setStates(v => {
      if (atAdded > -1) { // delete added
        delete v.stage.added[atAdded]
      } else if (isRestore) { // restore removed
        v.stage.removed.delete(i)
      } else { // to remove
        v.stage.removed.add(i)
      }
      return {...v, touched: { ls: Object.keys(v.stage.added).length > 0 || v.stage.removed.size > 0 } as any}
    })

    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: ExclusionRule, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined
  }
  return (
    <Box
      sx={EditorLineSx}
    >
      <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
        <Typography variant="subtitle2">Exclusion Rules</Typography>
        <Tooltip title="Add Exclusion Rule">
          <IconButton color="primary"
            onClick={() => setStates(v => {
              v.stage.added.push({ words: [], regex: null, excludeInfoTypes: null })
              return {...v, touched: { ls: true} as any}
            })}
          >
            <AddIcon /></IconButton>
        </Tooltip>
      </Stack>
      <Alert severity="info" sx={{ mb: 1 }}>
        An exclusion rule is used to exclude from this info type any contents that match certain keywords, patterns, or other info types.
      </Alert>
      <List disablePadding>
        {states.stage.added.map((d, i) => (
          <ExclusionRuleLine parentToken={token} className="added" key={i} r_key={`a[${i}]`} prevData={d} actionButton={actionBtn(d, i)} />
        )).filter(d => !!d).reverse()}
      </List>
      <List disablePadding>
        {states.prevData.map((d, i) => (
          <ExclusionRuleLine parentToken={token} className={classOf(d, i)} key={i} r_key={`r[${i}]`} prevData={d} actionButton={actionBtn(d, i)} />
        ))}
      </List>
    </Box>
  )
}

export default ExclusionRuleEditor;
